/**
 * Internal Messaging (Teams-style IM) service layer.
 *
 * Wraps the `im.rpc_im_*` SECURITY DEFINER RPCs introduced in
 * 20260704000000_im_init.sql. ALL authorization (tenant scope, active
 * participant, wholesaler im_enabled switch) lives in the SDFs and the
 * select-only RLS policies — this module is a pure typed wire layer.
 *
 * Realtime: `subscribeToConversationMessages` uses the same RLS-gated
 * `postgres_changes` pattern as the clients inbox; consumers should ALSO
 * keep a polling `refetchInterval` (see IM_*_POLL_MS) as the fallback so
 * the feature degrades gracefully when the Realtime socket is unavailable.
 *
 * Generated `Database` types lag this migration (codegen runs against a
 * local stack), hence the `as never` RPC casts — same precedent as
 * `clients.ts` / `calendar.ts`.
 */

import { supabase } from '../client';
import type { KeysetCursor } from '../pagination';

// =========================================================================
// Polling cadences (shop-inbox precedent: packages/shop-ui/src/inboxPolling.ts)
// =========================================================================

export const IM_MESSAGES_POLL_MS = 5_000;
export const IM_CONVERSATIONS_POLL_MS = 15_000;
export const IM_TYPING_POLL_MS = 3_000;
export const IM_PRESENCE_POLL_MS = 30_000;
export const IM_HEARTBEAT_MS = 60_000;
/** Client-side throttle between rpc_im_set_typing(true) calls. */
export const IM_TYPING_THROTTLE_MS = 3_000;

// =========================================================================
// Wire types — mirror SDF return shapes one-for-one.
// =========================================================================

export type ImConversationType = 'direct' | 'group' | 'wholesaler';
export type ImPresenceState =
  | 'auto'
  | 'available'
  | 'busy'
  | 'away'
  | 'do_not_disturb'
  | 'offline';
/** What the server resolves a user's presence to (manual > derived). */
export type ImEffectiveState =
  | 'available'
  | 'busy'
  | 'away'
  | 'do_not_disturb'
  | 'offline';

export interface ImConversationSummary {
  id: string;
  org_id: string;
  conversation_type: ImConversationType;
  wholesaler_id: string | null;
  title: string | null;
  peer_user_id: string | null;
  last_message_at: string;
  last_message_preview: string | null;
  last_message_sender_name: string | null;
  /** Capped at 100 server-side. */
  unread_count: number;
  participant_count: number;
  notifications_enabled: boolean;
  muted_until: string | null;
  my_role: 'owner' | 'member';
  my_last_read_at: string | null;
}

export interface ImParticipant {
  user_id: string;
  display_name: string;
  role: 'owner' | 'member';
  is_wholesaler_seat: boolean;
  joined_at: string;
  left_at: string | null;
  last_read_at: string | null;
}

export interface ImConversationDetail {
  id: string;
  org_id: string;
  conversation_type: ImConversationType;
  wholesaler_id: string | null;
  title: string | null;
  created_by: string | null;
  created_at: string;
  archived_at: string | null;
  participants: ImParticipant[];
  me: {
    role: 'owner' | 'member';
    notifications_enabled: boolean;
    muted_until: string | null;
    last_read_at: string | null;
    last_read_message_id: string | null;
  } | null;
}

export interface ImMessage {
  id: string;
  conversation_id: string;
  sender_user_id: string | null;
  sender_display_name: string | null;
  body: string;
  parent_message_id: string | null;
  parent_preview: string | null;
  parent_sender_name: string | null;
  client_generated_id: string | null;
  created_at: string;
}

export interface ImUserStatus {
  user_id: string;
  state: ImPresenceState;
  status_text: string | null;
  last_active_at: string | null;
  in_meeting: boolean;
  after_hours: boolean;
  effective_state: ImEffectiveState;
}

export interface ImKeysetPage<T> {
  rows: T[];
  nextCursor: KeysetCursor | null;
}

interface WirePage {
  rows: unknown[] | null;
  next_cursor: { sortValue: string; id: string } | null;
}

function toPage<T>(data: unknown): ImKeysetPage<T> {
  const page = (data ?? { rows: [], next_cursor: null }) as WirePage;
  return {
    rows: (page.rows ?? []) as T[],
    nextCursor: page.next_cursor
      ? { sortValue: page.next_cursor.sortValue, id: page.next_cursor.id }
      : null,
  };
}

function imSchema() {
  return supabase().schema('im' as never);
}

function cursorToJson(cursor: KeysetCursor | null | undefined): unknown {
  if (!cursor) return null;
  return { sortValue: cursor.sortValue, id: cursor.id };
}

// =========================================================================
// Conversations
// =========================================================================

export async function createDirectConversation(
  orgId: string,
  peerUserId: string,
): Promise<string> {
  const { data, error } = await imSchema().rpc('rpc_im_create_direct' as never, {
    p_org_id: orgId,
    p_peer_user_id: peerUserId,
  } as never);
  if (error) throw error;
  return data as unknown as string;
}

export async function createGroupConversation(
  orgId: string,
  title: string,
  memberUserIds: string[],
): Promise<string> {
  const { data, error } = await imSchema().rpc('rpc_im_create_group' as never, {
    p_org_id: orgId,
    p_title: title,
    p_member_user_ids: memberUserIds,
  } as never);
  if (error) throw error;
  return data as unknown as string;
}

export async function createWholesalerConversation(
  wholesalerId: string,
): Promise<string> {
  const { data, error } = await imSchema().rpc(
    'rpc_im_create_wholesaler_conversation' as never,
    { p_wholesaler_id: wholesalerId } as never,
  );
  if (error) throw error;
  return data as unknown as string;
}

export interface ListImConversationsInput {
  /** Operator surface: the active org. Portal surface: omit (null). */
  orgId?: string | null;
  cursor?: KeysetCursor | null;
  pageSize?: number;
}

export async function listImConversations(
  input: ListImConversationsInput = {},
): Promise<ImKeysetPage<ImConversationSummary>> {
  const { data, error } = await imSchema().rpc('rpc_im_list_conversations' as never, {
    p_org_id: input.orgId ?? null,
    p_cursor: cursorToJson(input.cursor),
    p_page_size: input.pageSize ?? 30,
  } as never);
  if (error) throw error;
  return toPage<ImConversationSummary>(data);
}

export async function getImConversation(
  conversationId: string,
): Promise<ImConversationDetail> {
  const { data, error } = await imSchema().rpc('rpc_im_get_conversation' as never, {
    p_conversation_id: conversationId,
  } as never);
  if (error) throw error;
  return data as unknown as ImConversationDetail;
}

// =========================================================================
// Messages
// =========================================================================

export interface ListImMessagesInput {
  conversationId: string;
  cursor?: KeysetCursor | null;
  pageSize?: number;
}

export async function listImMessages(
  input: ListImMessagesInput,
): Promise<ImKeysetPage<ImMessage>> {
  const { data, error } = await imSchema().rpc('rpc_im_list_messages' as never, {
    p_conversation_id: input.conversationId,
    p_cursor: cursorToJson(input.cursor),
    p_page_size: input.pageSize ?? 50,
  } as never);
  if (error) throw error;
  return toPage<ImMessage>(data);
}

export interface SendImMessageInput {
  conversationId: string;
  body: string;
  parentMessageId?: string | null;
  /** Client-generated idempotency key; retries return the same message. */
  clientGeneratedId?: string | null;
}

export async function sendImMessage(input: SendImMessageInput): Promise<ImMessage> {
  const { data, error } = await imSchema().rpc('rpc_im_send_message' as never, {
    p_conversation_id: input.conversationId,
    p_body: input.body,
    p_parent_message_id: input.parentMessageId ?? null,
    p_client_generated_id: input.clientGeneratedId ?? null,
  } as never);
  if (error) throw error;
  return data as unknown as ImMessage;
}

export async function markImConversationRead(
  conversationId: string,
  messageId?: string | null,
): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_mark_read' as never, {
    p_conversation_id: conversationId,
    p_message_id: messageId ?? null,
  } as never);
  if (error) throw error;
}

export async function getImUnreadSummary(
  orgId?: string | null,
): Promise<{ unread_conversations: number }> {
  const { data, error } = await imSchema().rpc('rpc_im_unread_summary' as never, {
    p_org_id: orgId ?? null,
  } as never);
  if (error) throw error;
  return data as unknown as { unread_conversations: number };
}

// =========================================================================
// Participants + per-conversation notification prefs
// =========================================================================

export async function addImParticipants(
  conversationId: string,
  userIds: string[],
): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_add_participants' as never, {
    p_conversation_id: conversationId,
    p_user_ids: userIds,
  } as never);
  if (error) throw error;
}

export async function removeImParticipant(
  conversationId: string,
  userId: string,
): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_remove_participant' as never, {
    p_conversation_id: conversationId,
    p_user_id: userId,
  } as never);
  if (error) throw error;
}

export async function setImNotificationPrefs(
  conversationId: string,
  prefs: {
    notificationsEnabled?: boolean | null;
    mutedUntil?: string | null;
    clearMute?: boolean;
  },
): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_set_notification_prefs' as never, {
    p_conversation_id: conversationId,
    p_notifications_enabled: prefs.notificationsEnabled ?? null,
    p_muted_until: prefs.mutedUntil ?? null,
    p_clear_mute: prefs.clearMute ?? false,
  } as never);
  if (error) throw error;
}

// =========================================================================
// Typing (ephemeral)
// =========================================================================

export async function setImTyping(
  conversationId: string,
  isTyping: boolean,
): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_set_typing' as never, {
    p_conversation_id: conversationId,
    p_is_typing: isTyping,
  } as never);
  if (error) throw error;
}

export interface ImTypingRow {
  conversation_id: string;
  user_id: string;
  expires_at: string;
}

/** RLS-gated direct read; only fellow participants see typing rows. */
export async function listImTyping(conversationId: string): Promise<ImTypingRow[]> {
  const { data, error } = await imSchema()
    .from('typing_states' as never)
    .select('conversation_id, user_id, expires_at')
    .eq('conversation_id', conversationId)
    .gt('expires_at', new Date().toISOString())
    .limit(50);
  if (error) throw error;
  return (data ?? []) as unknown as ImTypingRow[];
}

// =========================================================================
// Presence / status
// =========================================================================

export async function imHeartbeat(): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_heartbeat' as never, {} as never);
  if (error) throw error;
}

export async function setImStatus(
  state: ImPresenceState,
  statusText?: string | null,
  expiresAt?: string | null,
): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_set_status' as never, {
    p_state: state,
    p_status_text: statusText ?? null,
    p_expires_at: expiresAt ?? null,
  } as never);
  if (error) throw error;
}

export async function getImStatuses(
  userIds: string[],
  orgId?: string | null,
): Promise<ImUserStatus[]> {
  if (userIds.length === 0) return [];
  const { data, error } = await imSchema().rpc('rpc_im_get_statuses' as never, {
    p_user_ids: userIds.slice(0, 50),
    p_org_id: orgId ?? null,
  } as never);
  if (error) throw error;
  return (data ?? []) as unknown as ImUserStatus[];
}

// =========================================================================
// Wholesaler IM switch
// =========================================================================

export async function setWholesalerImEnabled(
  wholesalerId: string,
  enabled: boolean,
): Promise<void> {
  const { error } = await imSchema().rpc('rpc_im_set_wholesaler_im_enabled' as never, {
    p_wholesaler_id: wholesalerId,
    p_enabled: enabled,
  } as never);
  if (error) throw error;
}

/** Portal-side capability probe (UI gating only; backend re-enforces). */
export async function getImPortalEnabled(): Promise<boolean> {
  const { data, error } = await imSchema().rpc('rpc_im_portal_enabled' as never, {} as never);
  if (error) throw error;
  return Boolean(data);
}

// =========================================================================
// Realtime (postgres_changes; clients-inbox precedent). RLS gates events:
// non-participants receive nothing for the conversation.
// =========================================================================

export interface ImRealtimeMessageRow {
  id: string;
  conversation_id: string;
  sender_user_id: string | null;
  parent_message_id: string | null;
  body: string;
  created_at: string;
}

export function subscribeToConversationMessages(
  conversationId: string,
  onInsert: (row: ImRealtimeMessageRow) => void,
): { unsubscribe: () => void } {
  const sb = supabase();
  const channel = sb
    .channel(`im.messages:conversation:${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'im',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      (payload: { new?: unknown }) => {
        if (payload?.new) onInsert(payload.new as ImRealtimeMessageRow);
      },
    )
    .subscribe();
  return {
    unsubscribe: () => {
      void sb.removeChannel(channel);
    },
  };
}
